import { MDRenderer } from '@apia/components';
import { Box } from '@apia/theme';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import {
  TChatAssistantBlockSegment,
  TChatAssistantPanelConfig,
  TChatAssistantPanelSummary,
  TChatAssistantTextSegment,
  TChatAssistantTurnSegment,
  TChatAssistantTurnSnapshot,
  TChatAssistantTurnStatus,
} from '../types';
import { ChatMessage } from './ChatMessage';
import { TChatFeedbackForm } from '../TChatFeedbackForm';

const CHAT_ASSISTANT_TURN_MESSAGE_KEY = '__chatAssistantTurn';
const CHAT_ASSISTANT_PROGRESS_MESSAGE_KEY = '__chatAssistantProgress';
const CHAT_HISTORY_CONTENT_CHANGED_EVENT = 'chatHistoryContentChanged';

type TChatAssistantTurnCarrier = ChatMessage & {
  [CHAT_ASSISTANT_TURN_MESSAGE_KEY]?: ChatAssistantTurn;
};

type TChatAssistantProgressCarrier = ChatMessage & {
  [CHAT_ASSISTANT_PROGRESS_MESSAGE_KEY]?: true;
};

type TAppendAssistantBlockProps = {
  arguments?: string;
  content: string;
  id?: string;
  type: string;
};

type TAppendAssistantBlockResponseProps = {
  id: string;
  response: string;
};

type TFinishAssistantTurnOptions = {
  includeFinalMessage?: boolean;
};

type TAssistantTurnController = {
  addMessage(message: ChatMessage): void;
  currentIndex: number;
  state: {
    messages: ChatMessage[];
  };
};

type TChatAssistantTurnProps = {
  controller?: TAssistantTurnController;
  feedbackForm?: TChatFeedbackForm;
  finalMessage?: ChatMessage;
  panels?: TChatAssistantPanelConfig[];
  snapshot?: TChatAssistantTurnSnapshot;
};

type TChatAssistantPhraseSnapshot = TChatAssistantTurnSnapshot & {
  phraseSegmentId?: string;
};

function cleanSegmentClassName(type: string) {
  return type.replace(/[^a-zA-Z0-9_-]+/g, '-');
}

function requestChatHistoryAutoScroll() {
  if (typeof document !== 'undefined') {
    document.dispatchEvent(new Event(CHAT_HISTORY_CONTENT_CHANGED_EVENT));
  }
}

function attachChatMessageAssistantProgress(message: ChatMessage) {
  (message as TChatAssistantProgressCarrier)[
    CHAT_ASSISTANT_PROGRESS_MESSAGE_KEY
  ] = true;
  return message;
}

function isChatMessageAssistantProgress(message: ChatMessage) {
  return (
    (message as TChatAssistantProgressCarrier)[
      CHAT_ASSISTANT_PROGRESS_MESSAGE_KEY
    ] === true
  );
}

function getDurationMs(turn: ChatAssistantTurn) {
  const completedAt = turn.state.completedAt;
  if (!completedAt) {
    return undefined;
  }

  return Math.max(0, completedAt - turn.state.startedAt);
}

function getPanelSummary(
  config: TChatAssistantPanelConfig,
  turn: ChatAssistantTurn,
  segments: TChatAssistantTurnSegment[],
): TChatAssistantPanelSummary {
  return {
    blockCount: segments.filter((segment) => segment.kind === 'block').length,
    completedAt: turn.state.completedAt,
    durationMs: getDurationMs(turn),
    key: config.key,
    segments,
    startedAt: turn.state.startedAt,
    status: turn.state.status,
    textCount: segments.filter((segment) => segment.kind === 'text').length,
  };
}

function getPanelEntries(turn: ChatAssistantTurn) {
  return turn.panels
    .map((config) => ({
      config,
      segments: turn.state.segments.filter(
        (segment) => config.matchSegment(segment),
      ),
    }))
    .filter((entry) => entry.segments.length > 0);
}

function defaultRenderSegment(segment: TChatAssistantTurnSegment) {
  if (segment.kind === 'text') {
    return (
      <Box
        className="chatAssistantSegment chatAssistantSegment--text"
        key={segment.id}
      >
        <MDRenderer str={segment.content} />
      </Box>
    );
  }

  return (
    <Box
      className={`chatAssistantSegment chatAssistantSegment--block chatAssistantSegment--${cleanSegmentClassName(
        segment.type,
      )}`}
      key={segment.id}
    >
      <Box className="chatAssistantSegment__content">{segment.content}</Box>
      {segment.response && (
        <Box className="chatAssistantSegment__response">
          <MDRenderer str={segment.response} />
        </Box>
      )}
    </Box>
  );
}

function getPhraseDisplaySegment(segment: TChatAssistantTurnSegment) {
  if (segment.kind === 'block' && segment.response) {
    return {
      ...segment,
      content: segment.response,
      response: undefined,
    };
  }
  return segment;
}

const ChatAssistantStackSegment = observer(
  ({ segment }: { segment: TChatAssistantTurnSegment }) => (
    <Box
      className={`chatAssistantStackSegment chatAssistantStackSegment--${segment.kind}`}
    >
      {defaultRenderSegment(segment)}
    </Box>
  ),
);

const ChatAssistantPhraseSegment = observer(
  ({
    revision,
    segment,
  }: {
    revision: number;
    segment: TChatAssistantTurnSegment;
  }) => (
    <Box
      className="chatAssistantStackSegment chatAssistantStackSegment--block chatAssistantPhraseSegment"
      key={revision}
    >
      {defaultRenderSegment(getPhraseDisplaySegment(segment))}
    </Box>
  ),
);

const ChatAssistantPanel = observer(
  ({
    config,
    segments,
    turn,
  }: {
    config: TChatAssistantPanelConfig;
    segments: TChatAssistantTurnSegment[];
    turn: ChatAssistantTurn;
  }) => {
    const [expanded, setExpanded] = useState(false);
    const [bodyMounted, setBodyMounted] = useState(false);
    const summary = getPanelSummary(config, turn, segments);
    const meta = (config.getMeta?.(summary) ?? []).filter(Boolean);

    if (segments.length === 0) {
      return null;
    }

    const toggleExpanded = () => {
      if (expanded) {
        setExpanded(false);
        requestAnimationFrame(requestChatHistoryAutoScroll);
        return;
      }

      setBodyMounted(true);
      requestAnimationFrame(() => {
        setExpanded(true);
        requestChatHistoryAutoScroll();
      });
    };

    return (
      <Box className={`chatAssistantPanel chatAssistantPanel--${config.key}`}>
        <button
          aria-expanded={expanded}
          className="chatAssistantPanel__toggle"
          onClick={toggleExpanded}
          type="button"
        >
          <Box className="chatAssistantPanel__copy">
            <Box className="chatAssistantPanel__title">{config.title}</Box>
            {meta.length > 0 && (
              <Box className="chatAssistantPanel__meta">
                {meta.map((entry, index) => (
                  <Box className="chatAssistantPanel__metaItem" key={index}>
                    {entry}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          <Box aria-hidden className="chatAssistantPanel__chevron">
            {expanded ? '-' : '+'}
          </Box>
        </button>
        {bodyMounted && (
          <Box
            aria-hidden={!expanded}
            className={`chatAssistantPanel__bodyClip ${
              expanded
                ? 'chatAssistantPanel__bodyClip--expanded'
                : 'chatAssistantPanel__bodyClip--collapsed'
            }`}
            onTransitionEnd={(ev) => {
              if (ev.target !== ev.currentTarget) {
                return;
              }
              if (!expanded) {
                setBodyMounted(false);
              }
              requestChatHistoryAutoScroll();
            }}
          >
            <Box className="chatAssistantPanel__body">
              {segments.map((segment) => (
                <Box className="chatAssistantPanel__item" key={segment.id}>
                  {config.renderSegment
                    ? config.renderSegment(segment)
                    : defaultRenderSegment(segment)}
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  },
);

export class ChatAssistantTurn {
  private controller?: TAssistantTurnController;
  private finalMessage?: ChatMessage;
  private nextSegment = 0;
  private panelMessage?: ChatMessage;
  private phraseMessage?: ChatMessage;
  private phraseRevision = 0;
  private phraseSegmentId?: string;
  private nextIntermediateTextSegmentId?: string;
  private nextIntermediateTextStartsSegment = false;
  private transientMessages: ChatMessage[] = [];
  private transientMessagesBySegmentId = new Map<string, ChatMessage>();
  private feedbackForm: TChatFeedbackForm = 'stack';

  panels: TChatAssistantPanelConfig[] = [];

  state: TChatAssistantTurnSnapshot = {
    segments: [],
    startedAt: Date.now(),
    status: 'running',
  };

  constructor({
    controller,
    feedbackForm = 'stack',
    finalMessage,
    panels = [],
    snapshot,
  }: TChatAssistantTurnProps = {}) {
    this.controller = controller;
    this.feedbackForm = feedbackForm;
    this.finalMessage = finalMessage;
    this.panels = panels;

    if (snapshot) {
      const phraseSnapshot = snapshot as TChatAssistantPhraseSnapshot;
      this.state = {
        completedAt: snapshot.completedAt,
        segments: snapshot.segments.map((segment) => ({ ...segment })),
        startedAt: snapshot.startedAt,
        status: snapshot.status,
      };
      this.nextSegment = snapshot.segments.length;
      this.phraseSegmentId = phraseSnapshot.phraseSegmentId;
    }

    makeObservable(this, {
      state: observable,
    });
  }

  appendText(content: string) {
    this.appendIntermediateText(content);
  }

  beginIntermediateText(id?: string) {
    this.nextIntermediateTextSegmentId = id;
    this.nextIntermediateTextStartsSegment = true;
  }

  appendIntermediateText(content: string, id?: string) {
    if (this.state.status !== 'running') {
      return;
    }

    if (!content) {
      return;
    }

    const segmentId = id || this.nextIntermediateTextSegmentId;
    if (segmentId) {
      const existingSegment = this.state.segments.find(
        (segment): segment is TChatAssistantTextSegment =>
          segment.kind === 'text' && segment.id === segmentId,
      );

      if (existingSegment) {
        existingSegment.content = `${existingSegment.content}${content}`;
        this.refreshProgressMessage(existingSegment);
        this.nextIntermediateTextStartsSegment = false;
        this.nextIntermediateTextSegmentId = undefined;
        return;
      }
    }

    const lastSegment = this.state.segments[this.state.segments.length - 1];
    if (
      !segmentId &&
      lastSegment?.kind === 'text' &&
      !this.nextIntermediateTextStartsSegment
    ) {
      lastSegment.content = `${lastSegment.content}${content}`;
      this.refreshProgressMessage(lastSegment);
      return;
    }

    this.nextIntermediateTextStartsSegment = false;
    this.nextIntermediateTextSegmentId = undefined;
    const segment: TChatAssistantTextSegment = {
      at: Date.now(),
      content,
      id: segmentId || `text-${this.nextSegment++}`,
      kind: 'text',
    };
    this.state.segments.push(segment);
    this.showProgressMessage(segment);
  }

  appendBlock(block: TAppendAssistantBlockProps) {
    if (this.state.status !== 'running') {
      return;
    }

    const segment: TChatAssistantBlockSegment = {
      arguments: block.arguments,
      at: Date.now(),
      content: block.content,
      id: block.id || `block-${this.nextSegment++}`,
      kind: 'block',
      type: block.type,
    };
    this.state.segments.push(segment);
    this.showProgressMessage(segment);
  }

  appendBlockResponse(response: TAppendAssistantBlockResponseProps) {
    if (this.state.status !== 'running') {
      return;
    }

    const segment = this.state.segments.find(
      (current): current is TChatAssistantBlockSegment =>
        current.kind === 'block' && current.id === response.id,
    );

    if (!segment) {
      return;
    }

    segment.response = response.response;
    this.refreshProgressMessage(segment);
  }

  appendFinalText(content: string) {
    if (!content || !this.finalMessage) {
      return;
    }

    this.finalMessage.message = `${this.getFinalText()}${content}`;
    this.ensureFinalMessageVisible();
    requestChatHistoryAutoScroll();
  }

  createPanelMessage() {
    if (!this.panelMessage) {
      this.panelMessage = new ChatMessage(
        <AssistantTurnMessage turn={this} />,
        'customMessage',
      );
      attachChatMessageAssistantTurn(this.panelMessage, this);
    }

    return this.panelMessage;
  }

  finish(
    status: TChatAssistantTurnStatus = 'finished',
    finalText?: string,
    options: TFinishAssistantTurnOptions = {},
  ) {
    if (this.state.status !== 'running') {
      return;
    }

    this.setFinalText(finalText);
    this.state.status = status;
    this.state.completedAt = Date.now();
    this.collapseTransientMessages(options.includeFinalMessage);
  }

  getFinalText() {
    return typeof this.finalMessage?.message === 'string'
      ? this.finalMessage.message
      : '';
  }

  setFinalText(content?: string) {
    if (!content?.trim() || !this.finalMessage) {
      return;
    }

    this.finalMessage.message = content;
    this.ensureFinalMessageVisible();
  }

  toSnapshot(): TChatAssistantTurnSnapshot {
    return {
      completedAt: this.state.completedAt,
      phraseSegmentId: this.phraseSegmentId,
      segments: this.state.segments.map((segment) => ({ ...segment })),
      startedAt: this.state.startedAt,
      status: this.state.status,
    } as TChatAssistantTurnSnapshot;
  }

  /**
   * Creates a standalone progress phrase from the latest active feedback event.
   */
  public createPhraseMessage() {
    const segment = this.getPhraseSegment();
    if (!segment) {
      return undefined;
    }
    return attachChatMessageAssistantProgress(
      new ChatMessage(
        <ChatAssistantPhraseSegment
          revision={this.phraseRevision}
          segment={segment}
        />,
        'customMessage',
      ),
    );
  }

  private showProgressMessage(segment: TChatAssistantTurnSegment) {
    if (this.feedbackForm === 'none') {
      return;
    }
    if (this.feedbackForm === 'phrase') {
      this.showPhraseMessage(segment);
      return;
    }
    this.addStackMessage(segment);
  }

  private addStackMessage(segment: TChatAssistantTurnSegment) {
    if (!this.controller) {
      return;
    }

    const message = new ChatMessage(
      <ChatAssistantStackSegment segment={segment} />,
      'customMessage',
    );
    this.transientMessages.push(message);
    this.transientMessagesBySegmentId.set(segment.id, message);
    this.controller.addMessage(message);
    requestChatHistoryAutoScroll();
  }

  private refreshProgressMessage(segment: TChatAssistantTurnSegment) {
    if (this.feedbackForm === 'phrase') {
      this.refreshPhraseMessage(segment);
      return;
    }
    this.refreshStackMessage(segment);
  }

  private refreshStackMessage(segment: TChatAssistantTurnSegment) {
    const message = this.transientMessagesBySegmentId.get(segment.id);
    if (!message) {
      return;
    }

    message.message = <ChatAssistantStackSegment segment={segment} />;
    requestChatHistoryAutoScroll();
  }

  private showPhraseMessage(segment: TChatAssistantTurnSegment) {
    const controller = this.controller;
    if (!controller) {
      return;
    }

    this.phraseRevision++;
    this.phraseSegmentId = segment.id;
    const content = (
      <ChatAssistantPhraseSegment
        revision={this.phraseRevision}
        segment={segment}
      />
    );
    if (this.phraseMessage) {
      this.phraseMessage.message = content;
      requestChatHistoryAutoScroll();
      return;
    }

    this.phraseMessage = attachChatMessageAssistantProgress(
      new ChatMessage(content, 'customMessage'),
    );
    this.transientMessages.push(this.phraseMessage);
    controller.addMessage(this.phraseMessage);
    requestChatHistoryAutoScroll();
  }

  private refreshPhraseMessage(segment: TChatAssistantTurnSegment) {
    this.showPhraseMessage(segment);
  }

  private getPhraseSegment() {
    return (
      this.state.segments.find(
        (segment) => segment.id === this.phraseSegmentId,
      ) ?? this.state.segments[this.state.segments.length - 1]
    );
  }

  private collapseTransientMessages(includeFinalMessage = false) {
    const controller = this.controller;
    if (!controller) {
      return;
    }

    const replacements: ChatMessage[] = [];
    if (this.feedbackForm === 'stack' && getPanelEntries(this).length > 0) {
      replacements.push(this.createPanelMessage());
    }

    const finalText = this.getFinalText();
    if ((includeFinalMessage || finalText.trim()) && this.finalMessage) {
      replacements.push(this.finalMessage);
    }

    const messages = controller.state.messages;
    const transientSet = new Set(this.transientMessages);
    const shouldRemoveMessage = (message: ChatMessage) =>
      transientSet.has(message) ||
      message === this.finalMessage ||
      (this.feedbackForm === 'phrase' &&
        isChatMessageAssistantProgress(message));
    const firstTransientIndex = messages.findIndex((message) =>
      transientSet.has(message),
    );

    if (firstTransientIndex < 0) {
      controller.state.messages = [
        ...messages.filter((message) => !shouldRemoveMessage(message)),
        ...replacements,
      ];
      controller.currentIndex = controller.state.messages.length;
      this.transientMessages = [];
      this.transientMessagesBySegmentId.clear();
      this.phraseMessage = undefined;
      this.phraseSegmentId = undefined;
      requestChatHistoryAutoScroll();
      return;
    }

    controller.state.messages = [
      ...messages
        .slice(0, firstTransientIndex)
        .filter((message) => !shouldRemoveMessage(message)),
      ...replacements,
      ...messages
        .slice(firstTransientIndex)
        .filter((message) => !shouldRemoveMessage(message)),
    ];
    controller.currentIndex = controller.state.messages.length;
    this.transientMessages = [];
    this.transientMessagesBySegmentId.clear();
    this.phraseMessage = undefined;
    this.phraseSegmentId = undefined;
    requestChatHistoryAutoScroll();
  }

  private ensureFinalMessageVisible() {
    const controller = this.controller;
    if (!controller || !this.finalMessage) {
      return;
    }

    if (controller.state.messages.includes(this.finalMessage)) {
      return;
    }

    this.transientMessages.push(this.finalMessage);
    controller.addMessage(this.finalMessage);
  }
}

export function attachChatMessageAssistantTurn(
  message: ChatMessage,
  turn: ChatAssistantTurn,
) {
  (message as TChatAssistantTurnCarrier)[CHAT_ASSISTANT_TURN_MESSAGE_KEY] =
    turn;
}

export function getChatMessageAssistantTurn(message: ChatMessage) {
  return (message as TChatAssistantTurnCarrier)[
    CHAT_ASSISTANT_TURN_MESSAGE_KEY
  ];
}

export const AssistantTurnMessage = observer(
  ({ turn }: { turn: ChatAssistantTurn }) => {
    const entries = getPanelEntries(turn);

    if (entries.length === 0) {
      return null;
    }

    return (
      <Box className="chatAssistantTurn">
        {entries.map((entry) => (
          <ChatAssistantPanel
            config={entry.config}
            key={entry.config.key}
            segments={entry.segments}
            turn={turn}
          />
        ))}
      </Box>
    );
  },
);
